import { motion } from "framer-motion";
import { Layout, Paintbrush, RefreshCw, Rocket, Search, Smartphone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionGlow from "./SectionGlow";
import { ease, duration, stagger } from "@/lib/motion";
const services = [
  { icon: Layout, title: "Custom Web Design", desc: "Built from scratch around your brand. Designed to convert." },
  { icon: RefreshCw, title: "Website Redesigns", desc: "Old site dragging you down? We rebuild it for speed, SEO, and trust." },
  { icon: Rocket, title: "Landing Pages", desc: "High-converting pages built for ads, promotions, and seasonal pushes." },
  { icon: Paintbrush, title: "Brand Support", desc: "Logo, colors, and brand guidelines so you don't look like every other contractor." },
  { icon: Search, title: "SEO Foundations", desc: "On-page SEO so customers find you on Google, not your competitor." },
  { icon: Smartphone, title: "Mobile-First", desc: "Built for the phone first. Looks sharp everywhere else." },
];

const ServiceCard = ({ service, isMobile }: { service: typeof services[0]; isMobile: boolean }) => (
  <div
    className={`h-full flex items-start gap-4 rounded-lg border border-l-2 border-cream/5 border-l-transparent p-5 md:p-6 ${
      isMobile ? "bg-cream/5" : "bg-cream/5 hover:border-l-sage/50 hover:border-cream/10 hover:bg-cream/10 transition-[border-color,background-color] duration-300 group"
    }`}
  >
    <div className={`h-10 w-10 shrink-0 rounded-lg bg-sage/10 flex items-center justify-center ${!isMobile ? "group-hover:bg-sage/20 transition-colors duration-300" : ""}`}>
      <service.icon className={`h-5 w-5 text-sage ${!isMobile ? "group-hover:text-primary transition-colors duration-300" : ""}`} />
    </div>
    <div className="min-w-0">
      <h3 className="font-heading text-base md:text-lg text-cream mb-1">{service.title}</h3>
      <p className="text-sm text-cream/60 leading-relaxed">{service.desc}</p>
    </div>
  </div>
);

const ServicesSection = () => {
  const isMobile = useIsMobile();

  return (
    <section
      id="services"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-charcoal relative overflow-hidden lg:pt-[var(--navbar-height,4.75rem)]"
      style={{ zIndex: 4 }}
      data-dark-section
    >
      <SectionGlow placement="top-right" />

      <div className="container mx-auto relative z-10 py-16 md:py-20 lg:py-0">
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 0 : 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: duration.smooth, ease: ease.smooth }}
          className="mb-10 md:mb-16 max-w-xl"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage/90 mb-3 md:text-sm">What We Do</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-cream mb-4 font-bold leading-[1.1]">
            Everything Your Site Needs to{" "}
            <span className="text-accent-sweep">Land</span> Clients.
          </h2>
          <p className="text-cream/60 leading-relaxed md:text-lg">
            Everything you need to look credible online and grow your business.
          </p>
        </motion.div>

        {isMobile ? (
          <motion.div
            className="grid sm:grid-cols-2 gap-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: duration.smooth, ease: ease.smooth }}
          >
            {services.map((service) => (
              <ServiceCard key={service.title} service={service} isMobile />
            ))}
          </motion.div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: duration.smooth, delay: i * stagger, ease: ease.smooth }}
              >
                <ServiceCard service={service} isMobile={false} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
